import { some, forEach } from 'lodash';
import React from 'react';

import connect from 'components/lib/connect';
import FeatureNotification from './feature_notification';
import FeatureNotificationTypes from 'scripts/domain/models/agent_notification/feature_notification_types';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

const updateIVRSched = {
  feature: FeatureNotificationTypes.IN_APP_NOTIFICATION,
  roleFlags: ['teamManagement'],
  orgIds: [],
  key: 'updateIVRSched',
  text: '[Action Required] Update Your IVR Holiday Schedule',
  subtext:
    'It’s time to update your Gladly IVR and confirm the Schedule node (typically named “Holiday Check”) displays the correct year to ensure your IVR handles calls correctly during your holiday hours.',
  url: 'https://gladly.wistia.com/medias/iyot8ajovl',
  urlText: 'Watch Now',
  endAt: '2026-01-02T06:00:00.000Z',
};
const sidekickReportingClassroom = {
  feature: FeatureNotificationTypes.IN_APP_NOTIFICATION,
  roleFlags: ['teamManagement'],
  orgIds: [],
  key: 'sidekickReportingClassroom',
  text: 'New: Sidekick Reporting Classroom',
  subtext: 'Learn how to use Sidekick’s reporting tools to uncover insights, optimize workflows, and improve outcomes.',
  url: 'https://gladly.wistia.com/medias/ccomiqzidq',
  urlText: 'Watch Now',
  endAt: '2025-12-22T06:00:00.000Z',
};

const CURRENT_FEATURES = [updateIVRSched, sidekickReportingClassroom];

export function FeatureNotificationList({ agentPreferences, organization }) {
  const isFeatureEnabled = useIsFeatureEnabled();

  let featureNotifications = [];
  forEach(CURRENT_FEATURES, featureProps => {
    if (shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization)) {
      featureNotifications.push(<FeatureNotification {...featureProps} />);
    }
  });

  if (!featureNotifications.length) {
    return null;
  }

  return <React.Fragment>{featureNotifications}</React.Fragment>;
}

const FeatureNotificationListContainer = connect(mapStateToProps)(FeatureNotificationList);

function mapStateToProps({ getProvider }) {
  const agentPreferences = getProvider('agentPreferences').get();
  const organization = getProvider('auth').get()?.claims;

  return {
    agentPreferences,
    organization,
  };
}

export function useHasFeaturesToShow(agentPreferences, organization) {
  const isFeatureEnabled = useIsFeatureEnabled();
  return some(CURRENT_FEATURES, featureProps => {
    return shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization);
  });
}

function shouldShowNotification(featureProps, agentPreferences, isFeatureEnabled, organization) {
  const roleRestricted = featureProps.roleFlags && featureProps.roleFlags.length;
  const restrictedAndHasRole = some(featureProps.roleFlags, roleFlag => isFeatureEnabled(roleFlag));
  const flagRestricted = featureProps.flag;

  const organizationId = organization?.id || organization?.orgId || '';
  const orgRestricted = !!featureProps.orgIds.length;
  const belongsToOrg = some(featureProps.orgIds, orgId => orgId.toLowerCase() === organizationId.toLowerCase());

  const currentTime = new Date();
  const startRestricted = !!featureProps.startAt;
  const afterStart = startRestricted ? currentTime > new Date(featureProps.startAt) : true;
  const endRestricted = !!featureProps.endAt;
  const beforeEnd = endRestricted ? currentTime < new Date(featureProps.endAt) : true;

  return (
    (!orgRestricted || belongsToOrg) &&
    (!roleRestricted || restrictedAndHasRole) &&
    (!flagRestricted || isFeatureEnabled(featureProps.flag)) &&
    afterStart &&
    beforeEnd &&
    !agentPreferences.hasSeenFeatureNotification(featureProps.feature)
  );
}

export default FeatureNotificationListContainer;
